import { createSlice } from '@reduxjs/toolkit';

export const STOCK_ADJUSTMENT_DEFAULT_VISIBLE_COLUMNS = [
    'adjustment_number',
    'adjustment_date',
    'location',
    'item',
    'quantity',
];

const initialState = {
    list: [],
    current: null,
    loading: false,
    error: null,
    pagination: {
        current_page: 1,
        per_page: 10,
        total: 0,
        last_page: 1
    },
    filters: {},
    sort: {
        field: 'stock_adjustments.adjustment_date',
        direction: 'desc'
    },
    settings: null,
    isSettingsInitialized: false,
    classicFilters: {},

    filterLoading: false,
    isDefaultFilter: false,
    defaultFilter: {
        id: '',
        name: '',
        identifier: '',
        user_id: '',
        is_shared: false,
        is_readonly: false,
        save_filter: false,
        mark_default: false,
        rules: {
            condition: "and",
            children: []
        }
    },
    visibleColumns: [...STOCK_ADJUSTMENT_DEFAULT_VISIBLE_COLUMNS],
};

const stockadjustmentSlice = createSlice({
    name: 'stockadjustment',
    initialState,
    reducers: {
        setLoading: (state, action) => {
            state.loading = action.payload;
        },
        setError: (state, action) => {
            state.error = action.payload;
            state.loading = false;
        },
        setList: (state, action) => {
            state.list = action.payload.data || [];
            state.pagination = {
                current_page: action?.payload?.meta?.current_page || 1,
                per_page: action?.payload?.meta?.per_page || 10,
                total: action?.payload?.meta?.total || 0,
                last_page: action?.payload?.meta?.last_page || 1
            };
            state.loading = false;
            state.error = null;
        },
        setCurrent: (state, action) => {
            state.current = action.payload;
            state.loading = false;
            state.error = null;
        },
        setFilters: (state, action) => {
            state.filters = action.payload;
        },
        setSort: (state, action) => {
            state.sort = action.payload;
        },
        setPagination: (state, action) => {
            state.pagination = { ...state.pagination, ...action.payload };
        },
        setSettings: (state, action) => {
            state.settings = action.payload;
            state.isSettingsInitialized = true;
            if (action.payload?.per_page) {
                state.pagination.per_page = action.payload.per_page;
            }
            if (action.payload?.default_sort_field) {
                state.sort.field = action.payload.default_sort_field;
            }
            if (action.payload?.default_sort_direction) {
                state.sort.direction = action.payload.default_sort_direction;
            }
        },
        setDefaultFilter: (state, action) => {
            state.defaultFilter = action.payload;
        },
        setFilterLoading: (state, action) => {
            state.filterLoading = action.payload;
        },
        changeFilterCondition: (state, action) => {
            state.defaultFilter = {
                ...state.defaultFilter,
                rules: {
                    ...state.defaultFilter.rules,
                    condition: action.payload
                }
            };
        },
        addFilterCondition: (state, action) => {
            state.defaultFilter = {
                ...state.defaultFilter,
                rules: {
                    ...state.defaultFilter.rules,
                    children: [
                        ...state.defaultFilter.rules.children,
                        action.payload
                    ]
                }
            };
        },
        updateFilterConditionRule: (state, action) => {
            const updatedChildren = state.defaultFilter.rules.children.map((child, idx) =>
                idx === action.payload.index ? { ...child, query: { ...child.query, ...action.payload.data } } : child
            );
            state.defaultFilter = {
                ...state.defaultFilter,
                rules: {
                    ...state.defaultFilter.rules,
                    children: updatedChildren
                }
            };
        },
        updateFilterConditionValue: (state, action) => {
            const updatedChildren = state.defaultFilter.rules.children.map((child, idx) =>
                idx === action.payload.index ? { ...child, query: { ...child.query, value: action.payload.value } } : child
            );
            state.defaultFilter = {
                ...state.defaultFilter,
                rules: {
                    ...state.defaultFilter.rules,
                    children: updatedChildren
                }
            };
        },
        removeFilterCondition: (state, action) => {
            const filteredChildren = state.defaultFilter.rules.children.filter((_, idx) =>
                idx !== action.payload
            );
            state.defaultFilter = {
                ...state.defaultFilter,
                rules: {
                    ...state.defaultFilter.rules,
                    children: filteredChildren
                }
            };
        },
        clearCurrent: (state) => {
            state.current = null;
        },
        clearError: (state) => {
            state.error = null;
        },
        resetFiltersAndPagination: (state) => {
            state.pagination = {
                current_page: 1,
                per_page: 10,
                total: 0,
                last_page: 1
            };
            state.sort = {
                field: 'stock_adjustments.adjustment_date',
                direction: 'desc'
            };
            state.filters = {};
            state.classicFilters = {};
            state.visibleColumns = [...STOCK_ADJUSTMENT_DEFAULT_VISIBLE_COLUMNS];
        },
        setClassicFilters: (state, action) => {
            state.classicFilters = action.payload;
        },
        setVisibleColumns: (state, action) => {
            state.visibleColumns = action.payload;
        },
        setSelected: (state, action) => {
            state.settings = {
                ...state.settings,
                selected: {
                    ...state.settings.selected,
                    [action.payload.key]: action.payload.data
                }
            };
        }
    }
});

export const {
    setLoading: setStockAdjustmentLoading,
    setError: setStockAdjustmentError,
    setList: setStockAdjustmentList,
    setCurrent: setStockAdjustmentCurrent,
    setSort: setStockAdjustmentSort,
    setPagination: setStockAdjustmentPagination,
    setSettings: setStockAdjustmentSettings,
    setFilters: setStockAdjustmentFilters,
    setDefaultFilter: setStockAdjustmentDefaultFilter,
    setFilterLoading: setStockAdjustmentFilterLoading,

    changeFilterCondition: changeStockAdjustmentFilterCondition,
    addFilterCondition: addStockAdjustmentFilterCondition,
    updateFilterConditionRule: updateStockAdjustmentFilterConditionRule,
    updateFilterConditionValue: updateStockAdjustmentFilterConditionValue,
    removeFilterCondition: removeStockAdjustmentFilterCondition,
    clearCurrent: clearStockAdjustmentCurrent,
    clearError: clearStockAdjustmentError,
    setSelected: setStockAdjustmentSelected,
    resetFiltersAndPagination: resetStockAdjustmentFiltersAndPagination,
    setClassicFilters: setStockAdjustmentClassicFilters,
    setVisibleColumns: setStockAdjustmentVisibleColumns,
} = stockadjustmentSlice.actions;

export default stockadjustmentSlice.reducer;
